import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import {
    OkObject,
    ToolsMissingDoctorRecordGridObject,
    ToolsMissingProcedureGridObject,
} from '@it2go/types';
import { filterFrag } from './types/filter.types';
import { GridInput } from '@it2go/data-grid';
import { PdfPrintObject } from '@libs/types/src/common/object-type/pdf-print.object';
import { exportFrag } from './types/common.types';
import {
    PensionsIncomeToolGridObject,
} from '@libs/types/src/services/object-type/tools/pensions-income-tool.object';
import {
    PensionsIncomeToolGenerateIncomingPaymentsInput,
} from '@libs/types/src/services/input-type/social/pensions-income-tool.generate-incoming-payments.input';
import {
    PensionsIncomeToolAssignPaymentsInput,
} from '@libs/types/src/services/input-type/social/pensions-income-tool.assign-payments.input';
import { SocialPaymentObject } from '@libs/types/src/services/object-type/folder/social-payment.object';
import {
    ContractualSurchargeIncomeToolGridObject,
} from '@libs/types/src/services/object-type/tools/contractual-surcharge-income-tool.object';
import {
    ContractualSurchargeIncomeToolGenerateIncomingPaymentsInput,
} from '@libs/types/src/services/input-type/social/contractual-surcharge-income-tool.generate-incoming-payments.input';
import {
    SocialContributionIncomeToolGridObject,
} from '@libs/types/src/services/object-type/tools/social-contribution-income-tool.object';
import {
    SocialContributionIncomeToolGenerateIncomingPaymentsInput,
} from '@libs/types/src/services/input-type/social/social-contribution-income-tool.generate-incoming-payments.input';
import {
    PensionsIncomeToolPrintPensionPayoutDocumentInput,
} from '@libs/types/src/services/input-type/social/pensions-income-tool.print-pension-payout-document.input';
import {
    FinancialDepositBulkToolCreateInput,
} from '@libs/types/src/services/input-type/social/financial-deposit-bulk-tool.create-input';
import {
    FinancialDepositBulkToolCreateResultObject,
} from '@libs/types/src/services/object-type/tools/financial-deposit-bulk-tool.object';
import {
    DeficitToolGridObject,
} from '@libs/types/src/services/object-type/tools/deficit-tool.object';
import {
    FinancialMovementsToolObject,
} from '@libs/types/src/services/object-type/tools/financial-movements-tool.object';
import {
    FinancialMovementsToolCashTransferInput,
} from '@libs/types/src/services/input-type/social/financial-movements-tool.cash-transfer.input';
import {
    FinancialMovementsToolOperationalPaymentInput,
} from '@libs/types/src/services/input-type/social/financial-movements-tool.operational-payment.input';
import {
    FinancialMovementsToolExportInput,
} from '@libs/types/src/services/input-type/social/financial-movements-tool.export-input';
import {
    FinancialMovementsToolSummaryInput,
} from '@libs/types/src/services/input-type/social/financial-movements-tool.summary-input';
import {
    CareContributionRefundToolInput,
} from '@libs/types/src/services/input-type/social/care-contribution-refund-tool.input';
import {
    CareContributionRefundToolObject,
} from '@libs/types/src/services/object-type/tools/care-contribution-refund-tool.object';

@Injectable({ providedIn: 'root' })
export class ToolsGqlService extends GqlServiceV2 {

    public getMissingProcedures(input: GridInput | null = null, fragment: string) {
        return this.query<ToolsMissingProcedureGridObject>(
            `
                query($input: GridInput) {
                    toolsMissingProcedures {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getMissingDoctorRecords(input: GridInput | null, fragment: string) {
        return this.query<ToolsMissingDoctorRecordGridObject>(`
                query($input: GridInput) {
                    toolsMissingDoctorRecords {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getPensionsIncomeTool(
        input: GridInput | null = null,
        fragment: string,
        dateFrom: Date | null = null,
        dateTo: Date | null = null,
    ) {
        return this.query<PensionsIncomeToolGridObject>(
            `
                query($input: GridInput, $dateFrom: DateTime, $dateTo: DateTime) {
                    pensionsIncomeTool {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input, dateFrom, dateTo },
        );
    }

    public generatePensionsIncomeToolIncomingPayments(
        input: PensionsIncomeToolGenerateIncomingPaymentsInput,
        frag: string = 'id',
    ) {
        return this.mutate<SocialPaymentObject[]>(
            `
                mutation($input: PensionsIncomeToolGenerateIncomingPaymentsInput!) {
                    pensionsIncomeTool {
                        generateIncomingPayments(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public assignPensionsIncomeToolPayments(
        input: PensionsIncomeToolAssignPaymentsInput,
        frag: string = 'id',
    ) {
        return this.mutate<SocialPaymentObject[]>(
            `
                mutation($input: PensionsIncomeToolAssignPaymentsInput!) {
                    pensionsIncomeTool {
                        assignPayments(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getContractualSurchargeIncomeTool(
        input: GridInput | null = null,
        fragment: string,
        dateFrom: Date | null = null,
        dateTo: Date | null = null,
    ) {
        return this.query<ContractualSurchargeIncomeToolGridObject>(
            `
                query($input: GridInput, $dateFrom: DateTime, $dateTo: DateTime) {
                    contractualSurchargeIncomeTool {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input, dateFrom, dateTo },
        );
    }

    public generateContractualSurchargeIncomeToolIncomingPayments(
        input: ContractualSurchargeIncomeToolGenerateIncomingPaymentsInput,
        frag: string = 'id',
    ) {
        return this.mutate<SocialPaymentObject[]>(
            `
                mutation($input: ContractualSurchargeIncomeToolGenerateIncomingPaymentsInput!) {
                    contractualSurchargeIncomeTool {
                        generateIncomingPayments(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialContributionIncomeTool(
        input: GridInput | null = null,
        fragment: string,
        dateFrom: Date | null = null,
        dateTo: Date | null = null,
    ) {
        return this.query<SocialContributionIncomeToolGridObject>(
            `
                query($input: GridInput, $dateFrom: DateTime, $dateTo: DateTime) {
                    socialContributionIncomeTool {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input, dateFrom, dateTo },
        );
    }

    public generateSocialContributionIncomeToolIncomingPayments(
        input: SocialContributionIncomeToolGenerateIncomingPaymentsInput,
        frag: string = 'id',
    ) {
        return this.mutate<SocialPaymentObject[]>(
            `
                mutation($input: SocialContributionIncomeToolGenerateIncomingPaymentsInput!) {
                    socialContributionIncomeTool {
                        generateIncomingPayments(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public printPensionPayoutDocument(
        input: PensionsIncomeToolPrintPensionPayoutDocumentInput,
        frag: string = exportFrag,
    ) {
        return this.query<PdfPrintObject>(
            `
                query($input: PensionsIncomeToolPrintPensionPayoutDocumentInput!) {
                    pensionsIncomeTool {
                        printPensionPayoutDocument(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createFinancialDepositBulkTool(
        input: FinancialDepositBulkToolCreateInput,
    ) {
        return this.mutate<FinancialDepositBulkToolCreateResultObject>(
            `
                mutation($input: FinancialDepositBulkToolCreateInput!) {
                    financialDepositBulkTool {
                        create(input: $input) {
                            items {
                                id
                            }
                            pdf {
                                ${exportFrag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getDeficitTool(
        input: GridInput | null = null,
        fragment: string,
    ) {
        return this.query<DeficitToolGridObject>(
            `
                query($input: GridInput) {
                    deficitTool {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getFinancialMovementsTool(
        input: GridInput | null = null,
        fragment: string,
    ) {
        return this.query<FinancialMovementsToolObject[]>(
            `
                query($input: GridInput) {
                    financialMovementsTool {
                        filter(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getFinancialMovementsToolFirstDate(serviceId: number) {
        return this.query<string | null>(
            `
                query($serviceId: Int!) {
                    financialMovementsTool {
                        firstMovementDate(serviceId: $serviceId)
                    }
                }
            `,
            { serviceId },
        );
    }

    public getFinancialMovementsToolOpeningBalance(input: FinancialMovementsToolSummaryInput) {
        return this.query<number>(
            `
                query($input: FinancialMovementsToolSummaryInput!) {
                    financialMovementsTool {
                        openingBalance(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public createFinancialMovementsToolCashTransfer(
        input: FinancialMovementsToolCashTransferInput,
        fragment: string,
    ) {
        return this.mutate<OkObject>(
            `
                mutation($input: FinancialMovementsToolCashTransferInput!) {
                    financialMovementsTool {
                        cashTransfer(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createFinancialMovementsToolOperationalPayment(
        input: FinancialMovementsToolOperationalPaymentInput,
        fragment: string,
    ) {
        return this.mutate<OkObject>(
            `
                mutation($input: FinancialMovementsToolOperationalPaymentInput!) {
                    financialMovementsTool {
                        createOperationalPayment(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }


    public exportFinancialMovementsTool(
        input: FinancialMovementsToolExportInput,
        fragment: string = exportFrag,
    ) {
        return this.query<PdfPrintObject>(
            `
                query($input: FinancialMovementsToolExportInput!) {
                    financialMovementsTool {
                        export(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getCareContributionRefundTool(input: CareContributionRefundToolInput, fragment: string) {
        return this.query<CareContributionRefundToolObject[]>(
            `
                query($input: CareContributionRefundToolInput!) {
                    careContributionRefundTool {
                        filter(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
